import React, {useEffect, useMemo} from "react";
import {useAppDispatch, useAppSelector} from "../../redux/store";
import {
    changeCityDSAC,
    changeDateDSAC,
    fetchDarkStoreDataTop,
    fetchDataDS,
    fetchGlobDS
} from "../../redux/darkstoreReducer";
import {
    selectAllStores,
    selectCityDS,
    selectDarkStoreData,
    selectDarkStoreWeeks,
    selectDateDS, selectGlobDS,
    selectTop
} from "../../common/selectors";
import {DarkStoreFirstColumnChart} from "./darkStoreFirstColumnChart";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/ru_RU";
import dateIcon from "../../assets/datePickerIcon.svg";
import closeIcon from "../../assets/close.svg";
import {Card, DatePicker, DatePickerProps, Select} from "antd";
import {RangePickerProps} from "antd/es/date-picker";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import updateLocale from "dayjs/plugin/updateLocale";
import {citiesDS} from "../../common/darkStoreOptions";
import {DarkStoreList} from "./darkStoreList";
import {DarkStoreTop} from "./darkStoreTop";
import {formatWeekToArray} from "../../common/formatWeekToISODate";
import {Drugs} from "./drugs";
import {GlobalIndicators} from "./globalIndicators";
import styles from './darkStorePage.module.sass'
import {startSFCatOneBlock} from "../../common/optionsForDate";

dayjs.extend(customParseFormat)
// Подключаем расширение для парсинга пользовательских форматов дат

dayjs.extend(advancedFormat)
// Подключаем расширение для расширенного форматирования дат

dayjs.extend(weekday)
// Подключаем расширение для работы с днями недели

dayjs.extend(localeData)
// Подключаем расширение для работы с локалями

dayjs.extend(weekOfYear)
// Подключаем расширение для работы с неделями года

dayjs.extend(weekYear)
// Подключаем расширение для работы с неделями и годами

dayjs.extend(updateLocale);
// Подключаем расширение для обновления локали

dayjs.updateLocale('zh-cn', {
    weekStart: 1,
});
// Устанавливаем начало недели на понедельник для dayjs

export const DarkStorePage = () => {
    // Получаем функцию dispatch для отправки экшенов в redux
    const dispatch = useAppDispatch()
    // Деструктурируем компонент RangePicker из DatePicker
    const {RangePicker} = DatePicker;
    // Получаем данные дарксторов из redux
    const data = useAppSelector(selectDarkStoreData)
    // Получаем список недель из redux
    const weeks = useAppSelector(selectDarkStoreWeeks)
    // Получаем все данные по дарксторам
    const allData = useAppSelector(selectAllStores)
    // Получаем выбранный диапазон дат
    const date = useAppSelector(selectDateDS)
    // Получаем топовые данные
    const top = useAppSelector(selectTop)
    // Получаем выбранный город
    const city = useAppSelector(selectCityDS)
    // Получаем глобальные показатели
    const globData = useAppSelector(selectGlobDS)

    // Кастомное форматирование даты для отображения в RangePicker
    const customFormat = (value: any) => {
        return value.format("YYYY-MM-DD ");
    }

    // Обработчик изменения диапазона дат
    const onChange = (
        value: DatePickerProps['value'] | RangePickerProps['value'],
        dateString: [string, string] | string,
    ) => {
        // Проверяем, что dateString является массивом (для RangePicker)
        if (Array.isArray(dateString)) {
            const startOfWeek = dayjs(dateString[0]).startOf('week').add(0, 'day'); // Начало недели (понедельник)
            const endOfWeek = dayjs(dateString[1]).endOf('week').add(0, 'day'); // Конец недели (воскресенье)

            const start = startOfWeek.format('YYYY-MM-DD');
            const end = endOfWeek.format('YYYY-MM-DD');

            dispatch(changeDateDSAC([start, end])); // Обновляем диапазон дат в redux
        }
    };

    // Эффект для загрузки топовых данных по дарксторам при изменении города, даты или недель
    useEffect(() => {
        if (weeks.length) {
            const formatWeek = formatWeekToArray(weeks[weeks.length - 1]); // Получаем диапазон дат для последней недели
            dispatch(fetchDarkStoreDataTop({city, end_date_to: formatWeek[1], start_date_from: formatWeek[0]}));
        }
    }, [city, date, weeks]);

    // Эффект для загрузки данных по дарксторам и глобальных показателей при изменении даты или города
    useEffect(() => {
        dispatch(fetchDataDS({city, end_date_to: date[1], start_date_from: date[0], offset: 0, type: 'page'}));
        if (!globData) {
            dispatch(fetchGlobDS({start_date_from: date[0], end_date_to: date[1]}));
        }
    }, [date, city, dispatch, globData]);

    // Опции для селектора городов, добавляем вариант "Все"
    const options = useMemo(() => {
        const opts = citiesDS.map((el) => ({label: el, value: el}));
        opts.unshift({label: 'Все', value: ''});
        return opts;
    }, [citiesDS]);

    // Обработчик изменения города
    const onChangeSelect = (value: string) => {
        dispatch(changeCityDSAC(value))
    }

    // Фильтрация данных по последней неделе
    const filteredAllData = useMemo(() => {
        return weeks.length && allData?.filter((el) => el.week_number === weeks[weeks.length - 1]);
    }, [weeks, allData]);



    return (
        <div>
            {/* Разделитель для визуального оформления */}
            <div className={styles.divider}></div>
            {/* Карточка с фильтрами и глобальными показателями */}
            <Card className={styles.card}
                  style={{position: 'sticky', top: 0, zIndex: 10, width: '100%', paddingLeft: 20, paddingRight: 20}}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <div>
                        {/* Выбор диапазона дат и города */}
                        <RangePicker defaultValue={[dayjs(date[0]), dayjs(date[1])]}
                                     style={{maxWidth: '320px', marginRight: 20}}
                                     picker={'week'}
                                     value={[dayjs(date[0]), dayjs(date[1])]}
                                     locale={locale}
                                     suffixIcon={<img src={dateIcon} alt={'select'}/>} // Иконка для выбора даты
                                     allowClear={{
                                         clearIcon: <img className={styles.closeIcon} src={closeIcon} alt={'clear'}/>
                                     }}
                                     format={customFormat}
                                     onChange={onChange}
                        />
                        <Select onChange={onChangeSelect} value={city} options={options} style={{width: 300}}/>
                    </div>

                    {/* Блок с глобальными показателями по дарксторам */}
                    {globData && <div style={{textAlign: 'right', display: 'flex', gap: 40, color: 'white'}}>
                        <div>
                            <div>{`Общее количество дарксторов: ${globData?.count}`}</div>
                            <div>{`Среднее SKU на даркстор: ${globData?.middle}`}</div>
                        </div>
                        <div>
                            <div>{`Активных: ${globData?.active}`}</div>
                            <div>{`Закрытых: ${globData?.count - globData?.active}`}</div>
                        </div>
                    </div>}
                </div>
            </Card>
            {/* График по дарксторам */}
            <DarkStoreFirstColumnChart data={data} weeks={weeks}/>
            {/* Список дарксторов по последней неделе */}
            {filteredAllData && <DarkStoreList data={filteredAllData}/>}
            <div className={styles.topDrugsBlock}>
                <Card className={styles.card}>
                    {/* Топовые показатели по дарксторам */}
                    {top && <DarkStoreTop data={top}/>}
                </Card>
                <Card className={styles.card}>
                    {/* Список препаратов по дарксторам */}
                    {filteredAllData && <Drugs data={filteredAllData}/>}
                </Card>
            </div>
            {/* Глобальные индикаторы по последней неделе */}
            {weeks.length && <GlobalIndicators week={weeks[weeks.length - 1]}/>}
        </div>
    );
};
